/**
 * Add-on unit types — one definition, used by every screen that prices an add-on.
 *
 * The unit decides the QUANTITY an add-on multiplies by:
 *   PerPanel  → the package's panel count      (RM 170 /panel × 24 panels)
 *   PerKwp    → the system size in kWp         (RM 150 /kWp × 12.9 kWp)   ← 2026-09-28
 *   PerItem   → whatever the agent types       (flat, qty editable)
 *   PerYear   → whatever the agent types       (flat, qty editable)
 *   (none)    → display only, free-text price
 *
 * Price syntax is unchanged: "700+170" means RM700 setup + RM170 per unit, so the
 * line total is always `base + rate × qty`.
 *
 * This file exists because the unit was previously decided by a ternary repeated in
 * six components — addonsTab, residentialSolarCalculatorV2, RoiSearcherView,
 * cscCombinedTab, cscMeterRow and formGenerator. Adding a unit meant finding all
 * twenty-odd of them, and missing one silently priced an add-on at qty 1. Adding the
 * next unit should mean editing this file alone.
 */

export const ADDON_UNIT_TYPES = [
    { value: '', label: '— None (display only, free-text price) —', word: 'item', suffix: '' },
    { value: 'PerPanel', label: 'Per Panel (× panel count)', word: 'panel', suffix: '/panel' },
    { value: 'PerKwp', label: 'Per kWp (× system size)', word: 'kWp', suffix: '/kWp' },
    { value: 'PerItem', label: 'Per Item (flat)', word: 'item', suffix: '/item' },
    { value: 'PerYear', label: 'Per Year (flat)', word: 'year', suffix: '/year' },
];

const byValue = (u) => ADDON_UNIT_TYPES.find(t => t.value === (u || '')) || ADDON_UNIT_TYPES[0];

/** "/panel", "/kWp", "/year", "/item" — or '' when the add-on has no unit. */
export const unitSuffix = (unitType) => (unitType ? byValue(unitType).suffix : '');

/** The singular noun for prose: "RM 170 per panel", "RM 150 per kWp". */
export const unitWord = (unitType) => byValue(unitType).word;

/** Column/label text: "Per panel", "Per kWp", … */
export const unitLabel = (unitType) => {
    if (!unitType) return '—';
    const t = byValue(unitType);
    return 'Per ' + t.word;
};

/** Does this unit multiply by a quantity at all? (A 'none' add-on never does.) */
export const isQtyBearing = (unitType) =>
    unitType === 'PerPanel' || unitType === 'PerKwp' || unitType === 'PerItem' || unitType === 'PerYear';

/**
 * The quantity an add-on should default to for a given system.
 * PerPanel follows the panel count, PerKwp follows the kWp (fractional on purpose —
 * a 12.9 kWp system is charged for 12.9, not 12 or 13), everything else is one.
 */
export function defaultQtyFor(unitType, { panels = 0, kwp = 0 } = {}) {
    if (unitType === 'PerPanel') return Math.max(0, Number(panels) || 0);
    if (unitType === 'PerKwp') return Math.max(0, Number(kwp) || 0);
    return 1;
}

/** kWp from a panel count when the caller only has panels and a wattage. */
export const kwpFromPanels = (panels, panelWatt) =>
    ((Number(panels) || 0) * (Number(panelWatt) || 0)) / 1000;
